import { apiFetch } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`

export interface StatsQuery {
  startDate: string
  endDate: string
  groupBy?: 'MONTHLY' | 'WEEKLY' | 'DAILY' | 'HOURLY' | 'HALF_HOURLY' | 'LINE_ITEM' | 'ORDER' | 'ADVERTISER' | 'CREATIVE' | 'TEAM' | 'MARKETPLACE_PRODUCT' | 'AD_UNIT' | 'SITE' | 'EARNINGS_ACCOUNT' | 'TIER'
  currency?: string
  earningsAccountId?: string
  advertiserId?: string
  orderId?: string
  lineItemId?: string
  creativeId?: string
  adUnitId?: string
  siteId?: string
  includeChunkRange?: boolean
  showFilteredBidReasons?: boolean
  showLocationTypes?: boolean
  topN?: number
  timeout?: number
  all?: 'lineItem' | 'order' | 'creative' | 'advertiser' | 'earningsAccount' | 'site' | 'adUnit'
  includeAll?: boolean
  /** True for political ads only, false to leave them out; unset for both. */
  political?: boolean
  aggregateTo?: 'MONTHLY' | 'WEEKLY' | 'DAILY' | 'HOURLY' | 'HALF_HOURLY' | 'ARTICLE' | 'ORDER' | 'LINE_ITEM' | 'ADVERTISER' | 'CREATIVE' | 'TEAM' | 'MARKETPLACE_PRODUCT' | 'AD_UNIT' | 'SITE' | 'EARNINGS_ACCOUNT' | 'TIER'
}

export interface CurrencyAmount {
  currency: string
  amount: number
}

export interface StatsChunk {
  start: string
  end: string
  impressions: number
  renderedImpressions: number
  visibleImpressions: number
  viewableImpressions: number
  clicks: number
  cost: CurrencyAmount
  earnings: CurrencyAmount
  ctr: number
  viewability: number
  uniqueUsers: number
  [key: string]: unknown
}

export interface StatsItem {
  id: string
  name: string
  chunks: StatsChunk[]
  totals: StatsChunk
  [key: string]: unknown
}

export interface StatsTotals {
  impressions?: number
  clicks?: number
  rendered?: number
  viewables?: number
  ctr?: number
  viewability?: number
  matchRate?: number
  impressionRequests?: number
  positiveImpressionRequests?: number
  uniqueUsers?: number
  cost?: CurrencyAmount
  totalCost?: CurrencyAmount
  earnings?: CurrencyAmount
  eCpm?: CurrencyAmount
  revenueShare?: CurrencyAmount
  [key: string]: unknown
}

export interface StatsResponse {
  chunks: (StatsItem | StatsChunk)[]
  totals: StatsTotals
  chunkRange?: (string | { start: string; end: string })[]
  totalCount?: number
  [key: string]: unknown
}

/**
 * Every stat the targeting endpoint reports per targeting value. Costs are
 * left out entirely rather than zeroed when the line item has none.
 */
export interface TargetingStats {
  impressions: number
  clicks: number
  rendered: number
  viewables: number
  visibles: number
  immeasurableImpressions?: number
  immeasurableRendered?: number
  cost?: CurrencyAmount
  /** Custom event counts, keyed by event id. */
  customStats?: Record<string, { events: number; valueSum: number }>
  [key: string]: unknown
}

/**
 * What the targeting endpoint answers with: a map keyed by
 * "<EXPRESSION_TYPE>=<value>", one entry per targeting value that matched.
 *
 * This is its own shape — unlike every other stats endpoint there is no
 * chunks/totals envelope around it, just the map.
 */
export type TargetingTotals = Record<string, TargetingStats>

/** Targeting kinds the ad server records stats against. */
export type ExpressionType =
  | 'AD_UNIT'
  | 'DEVICE_PROPERTY'
  | 'USER_SEGMENT'
  | 'DATE'
  | 'KEY_VALUE'
  | 'SITE'
  | 'AD_UNIT_MATCHING_LABEL'
  | 'CATEGORY'
  | 'NAMED_LOCATION'
  | 'GEOSPATIAL'
  | 'DAY_PARTING'
  | 'RETARGETING'
  | 'KEYWORD'
  | 'IP_ADDRESS'
  | 'SITE_GROUP'
  | 'DOMAIN_NAME'
  | 'SEMANTIC_CATEGORY'
  | 'THIRD_PARTY_AUDIENCE'
  | 'PUBLISHER'
  | 'ARTICLE'
  | 'FIRST_PARTY_AUDIENCE'
  | 'SITE_COUNTRY'
  | 'WEATHER'
  | 'ORGANISATION'
  | 'SIMILAR_KEYWORD'
  | 'UNRECOGNISED'

/** One targeting value and what it delivered. */
export interface TargetingStatsRow {
  type: ExpressionType
  value: string
  stats: TargetingStats
}

function isTargetingStats(value: unknown): value is TargetingStats {
  return typeof value === 'object' && value !== null && 'impressions' in value
}

/**
 * Turns the "TYPE=value" keyed map the API returns into rows. Values may contain
 * their own "=" (KEY_VALUE=make=texta), so only the first one separates the type.
 */
export function toTargetingRows(totals: TargetingTotals | undefined): TargetingStatsRow[] {
  if (!totals) return []
  const rows: TargetingStatsRow[] = []
  for (const [key, stats] of Object.entries(totals)) {
    if (!isTargetingStats(stats)) continue
    const separator = key.indexOf('=')
    rows.push({
      type: (separator === -1 ? key : key.slice(0, separator)) as ExpressionType,
      value: separator === -1 ? '' : key.slice(separator + 1),
      stats,
    })
  }
  return rows
}

/** Groups targeting rows by targeting type, each sorted by impressions. */
export function groupTargetingRows(
  rows: TargetingStatsRow[]
): { type: ExpressionType; rows: TargetingStatsRow[] }[] {
  const groups = new Map<ExpressionType, TargetingStatsRow[]>()
  for (const row of rows) {
    const existing = groups.get(row.type)
    if (existing) existing.push(row)
    else groups.set(row.type, [row])
  }
  return [...groups.entries()]
    .map(([type, groupRows]) => ({
      type,
      rows: groupRows.sort((a, b) => b.stats.impressions - a.stats.impressions),
    }))
    .sort((a, b) => a.type.localeCompare(b.type))
}

/**
 * What the traffic and location endpoints answer with: one map per measure,
 * each counting a single stat type (impressions unless the path says another)
 * per value. Both share this shape, so both are read the same way.
 *
 * Traffic measures: `deviceType`, `osType`, `mobileBrand`, `app`.
 * Location measures: `country` (continents land here too) and `city`.
 */
export type MeasureTotals = Record<string, Record<string, number>>

/**
 * Chunk id the API gives the bucket everything past `topN` is summed into. It
 * takes the place of the group's own id, so a row carrying it is a total, not
 * a thing that can be named or linked to.
 */
export const ALL_OTHERS_ID = 'all-others-stats-group-id'

/** One article's row: the URL it was served against and what it delivered. */
export interface ArticleStatsRow {
  url: string
  /** True for the bucket summing every article past `topN`. */
  isOthers: boolean
  stats: Record<string, unknown>
}

/**
 * Pulls the article rows out of an ordinary stats envelope. Article chunks name
 * themselves with a plain URL string rather than an object reference, so they
 * cannot go through the grouped-stats reader.
 *
 * Rows come back busiest first with the all-others bucket pinned to the bottom,
 * where a total belongs however big it is.
 */
export function toArticleRows(response: StatsResponse | undefined): ArticleStatsRow[] {
  const chunks = (response?.chunks ?? []) as Record<string, unknown>[]
  return chunks
    .flatMap((chunk) =>
      typeof chunk.article === 'string'
        ? [{ url: chunk.article, isOthers: chunk.article === ALL_OTHERS_ID, stats: chunk }]
        : []
    )
    .sort((a, b) => {
      if (a.isOthers !== b.isOthers) return a.isOthers ? 1 : -1
      return ((b.stats.impressions as number) ?? 0) - ((a.stats.impressions as number) ?? 0)
    })
}

function toParams(query: StatsQuery): URLSearchParams {
  const params = new URLSearchParams({
    context: getContext(),
    startDate: query.startDate,
    endDate: query.endDate,
  })
  if (query.groupBy) params.set('groupBy', query.groupBy)
  if (query.currency) params.set('currency', query.currency)
  if (query.earningsAccountId) params.set('earningsAccountId', query.earningsAccountId)
  if (query.advertiserId) params.set('advertiserId', query.advertiserId)
  if (query.orderId) params.set('orderId', query.orderId)
  if (query.lineItemId) params.set('lineItemId', query.lineItemId)
  if (query.adUnitId) params.set('adUnitId', query.adUnitId)
  if (query.siteId) params.set('siteId', query.siteId)
  if (query.includeChunkRange != null) params.set('includeChunkRange', String(query.includeChunkRange))
  if (query.showFilteredBidReasons != null) params.set('showFilteredBidReasons', String(query.showFilteredBidReasons))
  if (query.showLocationTypes != null) params.set('showLocationTypes', String(query.showLocationTypes))
  if (query.topN != null) params.set('topN', String(query.topN))
  if (query.timeout != null) params.set('timeout', String(query.timeout))
  if (query.all) params.set('all', query.all)
  if (query.includeAll != null) params.set('includeAll', String(query.includeAll))
  if (query.aggregateTo) params.set('aggregateTo', query.aggregateTo)
  if (query.creativeId) params.set('creativeId', query.creativeId)
  if (query.political != null) params.set('political', String(query.political))
  return params
}

export const StatsApi = {
  get: async (query: StatsQuery): Promise<StatsResponse> => {
    const res = await apiFetch(`${API_BASE}/stats?${toParams(query)}`)
    if (!res.ok) await throwApiError(res, 'Failed to fetch stats')
    return await res.json()
  },

  /** Delivery broken down by the targeting that was matched, e.g. NAMED_LOCATION=Oslo. */
  getTargeting: async (query: StatsQuery): Promise<TargetingTotals> => {
    const res = await apiFetch(`${API_BASE}/stats/targeting?${toParams(query)}`)
    if (!res.ok) await throwApiError(res, 'Failed to fetch targeting stats')
    return await res.json()
  },

  /** Delivery per device type, OS, mobile brand and app. */
  getTraffic: async (query: StatsQuery): Promise<MeasureTotals> => {
    const res = await apiFetch(`${API_BASE}/stats/traffic?${toParams(query)}`)
    if (!res.ok) await throwApiError(res, 'Failed to fetch traffic stats')
    return await res.json()
  },

  /** Delivery per country the request came from. */
  getLocation: async (query: StatsQuery): Promise<MeasureTotals> => {
    const res = await apiFetch(`${API_BASE}/stats/location?${toParams(query)}`)
    if (!res.ok) await throwApiError(res, 'Failed to fetch location stats')
    return await res.json()
  },

  /** Delivery per article the ads were served against. Needs `aggregateTo=ARTICLE`. */
  getArticles: async (query: StatsQuery): Promise<StatsResponse> => {
    const res = await apiFetch(`${API_BASE}/stats/article?${toParams(query)}`)
    if (!res.ok) await throwApiError(res, 'Failed to fetch article stats')
    return await res.json()
  },
}
